import { TextFieldTypes } from '@ionic/core';
import {
  IonCol,
  IonIcon,
  IonInput,
  IonLabel,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap.css';
import { FormattedInput } from '@buttercup/react-formatted-input';
import React from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  FieldValues,
  RegisterOptions
} from 'react-hook-form';
import PasswordInput from '../PasswordInput';
import ErrorMessage from '../ErrorMessage';
import './style.scss';
import CountrySelect from '../CountrySelect';
import PhoneInput from '../PhoneInput';
import DateInput from '../DateInput';
import { dateValidator } from '../../utils/validators';
interface Option {
  value: string;
  name: string;
}

interface Props {
  errors?: FieldErrors<FieldValues>;
  type: 'options' | 'select' | 'file' | TextFieldTypes;
  controllerProps?: {
    name: string;
    control: Control;
    rules?: Exclude<
      RegisterOptions,
      'valueAsNumber' | 'valueAsDate' | 'setValueAs'
    >;
  } | null;
  inputProps?: {
    placeholder?: string;
    autocapitalize?: string;
    [key: string]: any;
  };
  selectProps?: {
    type: string;
    placeholder?: string;
    options?: Option[];
  };
  optionsProps?: {
    items?: Option[];
  };
  children?: React.ReactNode;
  onIonInput?: (
    e: CustomEvent<KeyboardEvent>,
    onChange: (...event: any[]) => void
  ) => void;
  label?: string | React.ReactNode;
  isMarginless?: boolean;
  isFormatted?: boolean;
  note?: string;
  hasSpecialPhoneInput?: boolean;
  hasOptionalWording?: boolean;
  isISOFormat?: boolean;
}

const Field: React.FC<Props> = ({
  controllerProps,
  errors,
  type,
  inputProps,
  selectProps,
  optionsProps,
  onIonInput,
  children,
  label,
  isMarginless = false,
  isFormatted = false,
  note,
  hasSpecialPhoneInput,
  hasOptionalWording,
  isISOFormat = false
}) => {
  const hasPopover = !!note;
  const getFileInput = (callback: (e: any) => void, n: string) => {
    let localOnChange;
    if (controllerProps) {
      localOnChange = (e: {
        target: { files: React.ChangeEvent<Element>[] };
      }) => {
        callback(e.target.files ? e.target.files[0] : null);
      };
    } else if (!controllerProps && inputProps?.onChange) {
      localOnChange = inputProps.onChange;
    }
    return (
      <input
        name={n}
        type={type}
        className={inputProps?.className}
        accept={inputProps?.accept}
        onChange={localOnChange}
      />
    );
  };
  const render = () => {
    switch (type) {
      case 'file':
        if (inputProps?.onChange && inputProps?.name) {
          return getFileInput(inputProps.onChange, inputProps.name);
        } else {
          console.error(
            'onChange and name props are required for file input on controllerless render'
          );
          return null;
        }
    }
  };
  let labelSuffix;
  const hasExtraSpace =
    (!controllerProps || !controllerProps.rules?.required) &&
    hasOptionalWording;
  if (controllerProps && controllerProps.rules?.required) {
    labelSuffix = '*';
  } else if (hasExtraSpace) {
    labelSuffix = <small>(facultatif)</small>;
  }
  const labelElement = label ? (
    <IonLabel className="Field-label">
      {label}
      {hasExtraSpace ? ' ' : ''}
      {labelSuffix}
      {hasPopover ? (
        <Tooltip
          overlay={note}
          overlayClassName="Field-popover"
          placement="top"
        >
          <span className="Field-icon">
            <IonIcon size="20" src={require('../../assets/icons/info.svg')} />
          </span>
        </Tooltip>
      ) : (
        ''
      )}
    </IonLabel>
  ) : null;
  const controllerRender = ({
    onChange,
    onBlur,
    ref,
    value,
    name
  }: ControllerRenderProps) => {
    switch (type) {
      case 'password':
        return (
          <PasswordInput
            name={name}
            placeholder={inputProps?.placeholder || 'Mot de passe*'}
            value={value}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            innerRef={ref}
          />
        );
      case 'options':
        return (
          <div className="flex Field-options">
            {optionsProps?.items?.map((option) => {
              return (
                <button
                  key={option.value}
                  className={`custom-input ${
                    option.value === value ? 'is-active' : ''
                  }`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    onChange(option.value);
                  }}
                >
                  {option.name}
                </button>
              );
            })}
          </div>
        );
      case 'select':
        if (selectProps?.type === 'country') {
          return (
            <CountrySelect
              name={name}
              innerRef={ref}
              defaultValue={{ value: 'fr', label: 'France' }}
              value={value}
              onChange={(value) => (value ? onChange(value) : null)}
              onBlur={onBlur}
              placeholder={selectProps?.placeholder || ''}
              className="ion-margin-bottom"
            />
          );
        } else if (
          selectProps &&
          selectProps.options &&
          selectProps.options.length > 0
        ) {
          return (
            <IonSelect
              name={name}
              value={value}
              okText="Sélectionner"
              cancelText="Annuler"
              onIonChange={(e) => onChange(e.detail.value)}
              onIonBlur={onBlur}
              placeholder={selectProps?.placeholder}
              interface="popover"
              ref={ref}
            >
              {selectProps.options.map((option) => (
                <IonSelectOption value={option.value}>
                  {option.name}
                </IonSelectOption>
              ))}
            </IonSelect>
          );
        } else {
          return null;
        }
      case 'datetime-local':
        return (
          <DateInput
            onChange={onChange}
            value={value}
            name={name}
            placeholder={inputProps?.placeholder}
            onBlur={onBlur}
            isISOFormat={isISOFormat}
          />
        );
      case 'file':
        return getFileInput(onChange, name);
      case 'text':
      case 'number':
      case 'email':
      case 'tel':
      default:
        if (!isFormatted && !hasSpecialPhoneInput) {
          return (
            <IonInput
              name={name}
              value={value}
              placeholder={inputProps?.placeholder}
              onIonInput={(e) => {
                let value = (e.target as HTMLInputElement).value;
                if (!onIonInput) {
                  onChange(value);
                } else {
                  onIonInput(e, onChange);
                }
              }}
              onIonBlur={onBlur}
              autocomplete="new-password"
              type={type}
              ref={ref}
            />
          );
        } else if (type === 'tel' && hasSpecialPhoneInput) {
          return (
            <PhoneInput
              name={name}
              value={value}
              placeholder={inputProps?.placeholder || ''}
              onChange={(value) => onChange(value)}
              onBlur={onBlur}
              innerRef={ref}
            />
          );
        } else {
          return (
            <FormattedInput
              name={name}
              placeholder={inputProps?.placeholder}
              className="custom-input"
              format={inputProps?.format}
              type={type}
              value={value}
              autoComplete="new-password"
              onChange={(_formatted: string, raw: string) => {
                if (raw) {
                  onChange(raw);
                  return raw;
                }
              }}
              ref={ref}
            />
          );
        }
    }
  };
  return (
    <IonCol class={`Field ${isMarginless ? 'Field--marginless' : ''}`}>
      {labelElement}
      {controllerProps ? (
        <>
          <Controller
            {...{
              ...(controllerProps as any),
              rules: {
                ...(controllerProps.rules || {}),
                ...(type === 'datetime-local' ? dateValidator : {})
              }
            }}
            render={controllerRender}
          />
          <ErrorMessage errors={errors} fieldName={controllerProps.name} />
        </>
      ) : (
        render()
      )}
      {children}
    </IonCol>
  );
};
export default Field;
